import { Link } from "@/components/site/LocaleLink";
import { Phone, Mail, MapPin, MessageCircle, Clock } from "lucide-react";
import { Logo } from "./Logo";
import { useLang, useL } from "@/context/LanguageContext";
import { CONTACT, WHATSAPP_ENABLED, PHONE_ENABLED } from "@/data/copy";
import { CATEGORIES } from "@/data/products";

export const Footer = () => {
  const { t } = useLang();
  const L = useL();
  const year = new Date().getFullYear();

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/products", label: t.nav.products },
    { to: "/about", label: t.nav.about },
    { to: "/support", label: t.nav.support },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <footer className="relative overflow-hidden bg-forest text-cream" data-testid="footer">
      <div className="noise pointer-events-none absolute inset-0 opacity-[0.04]" />
      <div className="container-x relative py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:pe-8">
            <Logo variant="footer" />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-cream/70">{t.footer.about}</p>
            {WHATSAPP_ENABLED && (
              <a
                href={`https://wa.me/${CONTACT.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            )}
          </div>

          <div>
            <h4 className="mb-5 font-display text-sm font-bold uppercase tracking-wider text-gold-light">
              {t.footer.quickLinks}
            </h4>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-cream/75 transition-colors hover:text-gold-light">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-5 font-display text-sm font-bold uppercase tracking-wider text-gold-light">
              {t.footer.productCats}
            </h4>
            <ul className="space-y-3">
              {CATEGORIES.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/products?category=${c.id}`}
                    className="text-sm text-cream/75 transition-colors hover:text-gold-light"
                  >
                    {L(c.name)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-5 font-display text-sm font-bold uppercase tracking-wider text-gold-light">
              {t.footer.contact}
            </h4>
            <ul className="space-y-4 text-sm text-cream/75">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span>{L(CONTACT.address)}</span>
              </li>
              {PHONE_ENABLED && (
                <li className="flex items-center gap-3">
                  <Phone className="h-4 w-4 shrink-0 text-gold" />
                  <a href={`tel:${CONTACT.phoneHref}`} dir="ltr" className="transition-colors hover:text-gold-light">
                    {CONTACT.phone}
                  </a>
                </li>
              )}
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-gold" />
                <a href={`mailto:${CONTACT.email}`} className="transition-colors hover:text-gold-light">
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span>{L(CONTACT.hours)}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-cream/10 pt-8 sm:flex-row sm:items-center">
          <p className="text-xs text-cream/70">{t.footer.fullName}</p>
          <p className="text-xs text-cream/70">
            © {year} Al-Abrad. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};
